/*
 * Copyright (c) 2026. Bolmate B.V.
 */

import {deleteFromLocal, getFromLocal, saveToLocal} from './storageUtil';

const DAY_MS = 24 * 60 * 60 * 1000;

export const SNOOZE_KEY_PREFIX = 'bolmate-snooze:';
export const DEFAULT_SNOOZE_COOLDOWNS_MS = [7 * DAY_MS, 30 * DAY_MS, 90 * DAY_MS];

export interface SnoozeConfig {
    featureKey: string;
    /** Narrows the dismissal to e.g. `${userId}:${accountId}`; omitted = per device. */
    scope?: string;
    /** Cooldown per dismissal count (1st, 2nd, ...); the last entry repeats. */
    cooldowns?: number[];
}

export interface Snooze {
    isSnoozed(): boolean;
    recordDismissal(): void;
    reset(): void;
}

interface SnoozeRecord {
    count: number;
    ts: number;
}

export function snoozeKey(featureKey: string, scope?: string): string {
    return scope ? `${SNOOZE_KEY_PREFIX}${featureKey}:${scope}` : `${SNOOZE_KEY_PREFIX}${featureKey}`;
}

function readRecord(key: string): SnoozeRecord | null {
    const raw = getFromLocal(key);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw) as Partial<SnoozeRecord>;
        return {
            count: typeof parsed.count === 'number' ? parsed.count : 0,
            ts: typeof parsed.ts === 'number' ? parsed.ts : 0
        };
    } catch {
        return null;
    }
}

/** `key` is the full storage key — use snoozeKey() unless a legacy key format
 *  must be preserved (e.g. the native push-prompt back-off). */
export function createSnooze(key: string, cooldowns: number[] = DEFAULT_SNOOZE_COOLDOWNS_MS): Snooze {
    return {
        isSnoozed(): boolean {
            const record = readRecord(key);
            if (!record || record.count < 1 || !cooldowns.length) return false;
            const cooldown = cooldowns[Math.min(record.count, cooldowns.length) - 1];
            return Date.now() - record.ts < cooldown;
        },
        recordDismissal(): void {
            const count = (readRecord(key)?.count ?? 0) + 1;
            saveToLocal(key, JSON.stringify({count, ts: Date.now()}));
        },
        reset(): void {
            deleteFromLocal(key);
        }
    };
}

export function createSnoozeFor(config: SnoozeConfig): Snooze {
    return createSnooze(snoozeKey(config.featureKey, config.scope), config.cooldowns);
}
